/**
 * Re-exports — the TanStack Query client lives here so consumers can
 * `import { queryClient } from '@/lib/api'` everywhere.
 */
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { useAuthStore } from '../../stores/auth-store';
import { invalidateVerifiedPrincipal } from '../verified-principal';
import { ApiError } from './client';

export { agentApi, agentKeys } from './agent';
export { authApi } from './auth';
export { ApiError, apiFetch, apiJson } from './client';
export { constitutionApi, constitutionKeys } from './constitution';
export { fsApi, fsKeys } from './fs';
export {
  invitationKeys,
  invitationsApi,
  workspaceMemberKeys,
  workspaceMembersApi,
} from './invitations';
export { kanbanApi, kanbanKeys, type ExactMessageDelivery } from './kanban';
export { learningApi, learningKeys } from './learning';
export {
  cardStepQueueApi,
  cardStepQueueKeys,
  stepQueueLine,
  type CardStepQueue,
  type CardStepQueueItem,
} from './card-step-queue';
export { projectKeys, projectsApi } from './projects';
export {
  taskQueueApi,
  taskQueueKeys,
  formatEta,
  queueReasonText,
  queueStatusLine,
  type QueueItem,
  type QueueVendor,
  type QueuePatchBody,
} from './task-queue';
export {
  sessionsApi,
  sessionsKeys,
  type AgentType,
  type V6Session,
  type V6SessionState,
  type V6SessionMode,
  type StartSessionOpts,
  // M-X8: cross-vendor switch.
  type Vendor,
  // M-X26: Auto / Orchestrate / Direct preference.
  type SubagentPreference,
  // WP-4: the multi-user session model.
  type CardTimelineEntry,
  type OwnVendorEntry,
  type OwnVendorSessionSummary,
  type VendorSeedPreview,
} from './sessions';
export {
  vendorsApi,
  vendorsKeys,
  type VendorStatus,
} from './vendors';
export {
  type ChatAuthorKind,
  type ChatEffort,
  type ChatMessageType,
  type ChatPermissionMode,
  type ChatPostBody,
  type ChatStreamEvent,
  type SessionChatMessage,
  sessionChatApi,
  sessionChatKeys,
} from './session-chat';
export {
  type PushSubscriptionRow,
  pushApi,
  pushKeys,
  type VapidKeyInfo,
} from './push';
export {
  type SessionEstimate,
  type SessionLoad,
  type SystemLoad,
  systemApi,
  systemKeys,
} from './system';
export {
  type DisconnectResult,
  type RegisterWebhookResult,
  type TelegramStatus,
  type TestConnectionResult,
  telegramApi,
  telegramKeys,
} from './telegram';
export {
  bulkApi,
  dependenciesApi,
  dependenciesKeys,
  viewsApi,
  viewsKeys,
} from './views';
export { workspaceApi, workspaceKeys } from './workspace';

/**
 * Global mutation-error surfacing (CHAOS item 10).
 *
 * Mutations that don't handle their own failure used to fail silently —
 * the user got no feedback. A single MutationCache `onError` now shows
 * ONE generic destructive toast for any unhandled mutation error.
 *
 * No double-toast: this generic toaster is a true FALLBACK. It fires only
 * for mutations that do NOT define their own `onError` handler. Any
 * mutation with its own `onError` (the ~42 that render their own
 * destructive toast / recovery, e.g. the session picker rollback) is
 * skipped here, because in TanStack Query v5 the cache-level `onError`
 * runs IN ADDITION to the per-mutation one. A mutation can also opt out
 * explicitly via `meta.skipGlobalErrorToast: true` (e.g. one that handles
 * its failure without an `onError` callback).
 *
 * The toast queue lives in React context (`useToaster`), which this
 * non-React handler can't reach directly, so we dispatch a `window`
 * CustomEvent that the mounted <Toaster> listens for and enqueues.
 */
export const MUTATION_ERROR_TOAST_EVENT = 'chaos:mutation-error-toast';

export interface MutationErrorToastDetail {
  title: string;
  description: string;
}

/** Opt-out flag a mutation sets on `meta` to suppress the generic toast. */
interface MutationErrorMeta {
  skipGlobalErrorToast?: boolean;
}

/** Shown when we have nothing more specific / useful to say. */
const GENERIC_MUTATION_ERROR = "That change didn't save. Please try again.";

/**
 * Collapse a raw error string into ONE clean clause for the toast: first line
 * only (so a stack trace never leaks), whitespace collapsed, length-capped,
 * trailing punctuation stripped so the caller can add its own.
 */
function cleanErrorMessage(raw: string): string {
  const firstLine = (raw.split('\n')[0] ?? '').replace(/\s+/g, ' ').trim();
  const capped = firstLine.length > 140 ? `${firstLine.slice(0, 137)}…` : firstLine;
  return capped.replace(/[.\s]+$/, '');
}

/**
 * Surface the REAL failure cause in the toast description when we have one,
 * instead of always hiding it behind the generic line. For an ApiError we use
 * the server message (+ HTTP status); for a plain Error its message; and we
 * fall back to the generic sentence only when there's nothing user-useful.
 * Always one clean sentence, never a stack trace.
 */
function describeMutationError(error: unknown): string {
  if (error instanceof ApiError) {
    const msg = error.message?.trim() ?? '';
    // `http_<status>` / a bare "Request failed" carries no real signal — fall
    // back to a status-only line rather than echoing a meaningless code.
    const useful = msg && !/^http_\d+$/i.test(msg) && msg.toLowerCase() !== 'request failed';
    if (useful) {
      const clause = cleanErrorMessage(msg);
      return error.status ? `${clause} (error ${error.status}).` : `${clause}.`;
    }
    if (error.status) return `The server rejected that change (error ${error.status}).`;
    return GENERIC_MUTATION_ERROR;
  }
  if (error instanceof Error) {
    const msg = error.message?.trim();
    if (msg) return `${cleanErrorMessage(msg)}.`;
  }
  return GENERIC_MUTATION_ERROR;
}

const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    // Fallback only: if the mutation defines its own onError handler it
    // already surfaces the failure (e.g. its own destructive toast), and
    // the cache-level handler runs IN ADDITION to it in TanStack Query v5,
    // so skip here to avoid double toasts.
    if (typeof mutation.options.onError === 'function') return;
    const meta = mutation.options.meta as MutationErrorMeta | undefined;
    if (meta?.skipGlobalErrorToast) return;
    if (typeof window === 'undefined') return;
    const detail: MutationErrorToastDetail = {
      title: 'Something went wrong',
      description: describeMutationError(error),
    };
    window.dispatchEvent(
      new CustomEvent<MutationErrorToastDetail>(MUTATION_ERROR_TOAST_EVENT, { detail }),
    );
  },
});

/**
 * Global query-error → unauthenticated bridge (auth-redirect fix,
 * 2026-06-14).
 *
 * AuthBootstrap's GET /api/auth/me can be served a stale 200 from the
 * service-worker cache while the real session is already dead — in that
 * case the store flips to `authenticated`, the board mounts, and every
 * subsequent board query 401s silently. Without this, the user is stuck
 * staring at a board shell that never populates.
 *
 * This QueryCache `onError` (mirrors the MutationCache one above; in
 * TanStack Query v5 a cache-level handler runs IN ADDITION to any
 * per-query one) watches for an ApiError with status 401 and flips the
 * auth store to `unauthenticated`. That transition is what the
 * `_authenticated` layout's reactive subscription listens for to send
 * the user to /login. We intentionally do NOT act on 403 here — a 403 is
 * an authorization (permission) failure on a still-valid session, not a
 * dead session, so logging the user out would be wrong.
 */
const queryCache = new QueryCache({
  onError: (error) => {
    if (error instanceof ApiError && error.status === 401) {
      // Idempotent: setUnauthenticated() is a no-op state-wise once the
      // store is already `unauthenticated`, and the layout subscription
      // only fires on the *transition* into it, so repeated 401s from a
      // burst of board queries cannot cause a redirect loop.
      invalidateVerifiedPrincipal('unauthenticated');
      useAuthStore.getState().setUnauthenticated();
    }
  },
});

export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        // Don't retry on auth errors — user has to re-login.
        if (error instanceof Error && error.name === 'ApiError') {
          const status = (error as { status?: number }).status;
          if (status === 401 || status === 403) return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: 0,
    },
  },
});
